/** Estructura de la aplicación: menú lateral, barra superior y contenido. */

import { useEffect, useState } from "react";
import { NavLink, Outlet, useLocation } from "react-router-dom";
import type { Config, SessionInfo } from "../api/types";
import { AccountMenu } from "./AccountMenu";
import { Icon, type IconName } from "./icons";
import { Topbar, type Miga } from "./Topbar";
import { may } from "../api/permissions";
import { AvisoClasificacion } from "./AvisoClasificacion";
import { AvisoCorrida } from "./AvisoCorrida";
import { StatusBadge } from "./StatusBadge";

interface NavItem {
  to: string;
  /** Rótulo fijo. Si falta, el título sale del catálogo por `reportKey`. */
  label?: string;
  /** Clave del catálogo de reportes: el título lo pone el servidor. */
  reportKey?: string;
  /** Icono del menú. Acompaña al rótulo; nunca lo sustituye. */
  icon: IconName;
  /** Coincidencia exacta de ruta; solo la necesita la raíz. */
  end?: boolean;
  /** Se marca «admin» en el menú. */
  admin?: boolean;
  /** Sin este permiso no se ofrece el enlace. */
  permission?: string;
}

/**
 * El menú: documentos y procesos, no vistas de BigQuery.
 *
 * La regla que lo gobierna: **una variante técnica, una conciliación o un nivel
 * de detalle de un reporte no genera entrada de menú**. Vive dentro del reporte
 * que explica, como pestaña o selector.
 *
 * Aplicarla baja de 28 enlaces en 7 grupos a 12 en 4. Lo que desapareció no se
 * borró: las once hojas del cierre son pestañas de `/closing`, el detalle del
 * flujo y del patrimonio son sub-pestañas de su estado, y los dos estados de
 * capa 1 son pestañas de `/audit`. Todas conservan su ruta y su exportación.
 *
 * El orden es el del proceso: primero el documento que se firma, después los
 * libros que lo sostienen, después los anexos fiscales, y al final el control.
 */
const NAV: { title: string; items: NavItem[] }[] = [
  {
    title: "Principal",
    items: [{ to: "/", label: "Panel", icon: "panel", end: true }],
  },
  {
    // El cierre es UN entregable con once hojas, así que es UN enlace. El
    // Balance de Comprobación no está aquí: `package.yml` lo marca
    // `support_only`, es decir soporte de los libros y no hoja del paquete.
    title: "Contabilidad",
    items: [
      { to: "/closing", label: "Estados Financieros", icon: "estados" },
      { to: "/reports/trial_balance", label: "Balance de Comprobación",
        reportKey: "trial_balance", icon: "balance" },
      { to: "/reports/journal_book", label: "Libro Diario", reportKey: "journal_book",
        icon: "diario" },
      // Va junto al Libro Diario porque sale de sus mismos apuntes, y por
      // separado porque es otro documento: el libro es cronológico, el
      // comprobante respalda cada partida y se archiva por asiento.
      { to: "/reports/journal_voucher", label: "Comprobante de Partidas",
        reportKey: "journal_voucher", icon: "diario" },
      { to: "/reports/general_ledger", label: "Libro Mayor", reportKey: "general_ledger",
        icon: "mayor" },
      // Tercer libro y no una vista de los otros dos: va por grupo de cuentas
      // y por día, con el saldo con el que cada día entra y sale.
      { to: "/reports/journal_ledger_book", label: "Libro Diario-Mayor",
        reportKey: "journal_ledger_book", icon: "mayor" },
      // Entrada propia y no una vista del Libro Diario. La regla contra
      // duplicar entradas habla de variantes TÉCNICAS —el mismo documento con
      // otras columnas—; esta lleva departamento y usuario, que ningún libro
      // tiene, y su destinatario es el auditor externo, no el archivo legal.
      { to: "/reports/accounting_movements", label: "Movimientos Contables",
        reportKey: "accounting_movements", icon: "diario" },
    ],
  },
  {
    // Rótulos CORTOS, y el nombre formal completo en el <h1> de la página.
    // «Libro de Ventas a Consumidor Final (IVA)» partía en dos líneas dentro del
    // menú, y tres enlaces de dos líneas rompen el ritmo de la columna entera.
    title: "IVA y anexos",
    items: [
      { to: "/reports/vat_purchase_book", label: "Compras", reportKey: "vat_purchase_book",
        icon: "compras" },
      {
        to: "/reports/vat_sales_taxpayer_book",
        label: "Ventas · Contribuyentes",
        reportKey: "vat_sales_taxpayer_book",
        icon: "ventas",
      },
      {
        to: "/reports/vat_sales_consumer_book",
        label: "Ventas · Consumidor Final",
        reportKey: "vat_sales_consumer_book",
        icon: "ventas",
      },
      { to: "/reports/f07_annexes", label: "Anexos F-07", reportKey: "f07_annexes",
        icon: "anexos" },
      // Bajo los anexos porque es uno de ellos, y aparte porque no sale de un
      // libro de IVA: los libros reportan lo que se declara y este, lo que se
      // dejó de declarar.
      { to: "/reports/f07_voided", label: "Documentos Anulados",
        reportKey: "f07_voided", icon: "anexos" },
    ],
  },
  {
    // Material de control: agrupado al final para que el trabajo del contador
    // no compita con el detalle técnico.
    title: "Administración",
    items: [
      { to: "/runs", label: "Corridas", admin: true, icon: "corridas" },
      { to: "/exceptions", label: "Excepciones de calidad", icon: "excepciones" },
      { to: "/audit", label: "Auditoría del mapping", icon: "auditoria" },
      // El membrete acaba impreso en documentos que se firman.
      { to: "/settings", label: "Membrete y firmas", permission: "settings:manage",
        icon: "membrete" },
    ],
  },
];

const PLEGADO = "sv.menu.plegado";

/**
 * La miga sale del MENÚ, no de una tabla paralela de títulos: si una entrada
 * cambia de rótulo, la miga cambia con ella. Gana la ruta declarada más larga
 * que sea prefijo de la actual, de modo que `/closing/esf` —una pestaña, que no
 * tiene entrada propia— hereda la ubicación de `/closing`.
 */
function migaDe(pathname: string, titulo: (item: NavItem) => string): Miga {
  let mejor: { grupo: string; item: NavItem } | null = null;
  for (const grupo of NAV) {
    for (const item of grupo.items) {
      const coincide = item.end
        ? pathname === item.to
        : pathname === item.to || pathname.startsWith(`${item.to}/`);
      if (!coincide) continue;
      if (mejor === null || item.to.length > mejor.item.to.length) {
        mejor = { grupo: grupo.title, item };
      }
    }
  }
  if (!mejor) return { pagina: "BSR Plataforma institucional" };
  return { seccion: mejor.grupo, pagina: titulo(mejor.item) };
}

export function Layout({ config, session }: { config: Config; session: SessionInfo }) {
  const titles = new Map(config.reports.map((report) => [report.key, report.title]));
  const { pathname } = useLocation();

  /**
   * El menú se pliega a la columna de iconos, y la elección sobrevive a la
   * recarga: en el Libro Diario, con quince columnas, 248 px de menú son 248 px
   * que no ve el contador. `localStorage` puede fallar —ventana privada, datos
   * bloqueados—, así que se lee y se escribe protegido y el valor por defecto
   * es el menú desplegado.
   */
  const [plegado, setPlegado] = useState(() => {
    try {
      return localStorage.getItem(PLEGADO) === "1";
    } catch {
      return false;
    }
  });

  useEffect(() => {
    try {
      localStorage.setItem(PLEGADO, plegado ? "1" : "0");
    } catch {
      /* Preferencia de comodidad: si no se puede guardar, no pasa nada. */
    }
  }, [plegado]);

  const titulo = (item: NavItem): string =>
    item.label ?? titles.get(item.reportKey ?? "") ?? "";

  /**
   * Ocultar un enlace es COMODIDAD, no autorización: el permiso lo exige el
   * `Depends(require(...))` del endpoint, que devuelve 403 sin él. Esto solo
   * evita ofrecer una pantalla que ya sabemos que va a fallar.
   *
   * Un enlace a un reporte que el catálogo no trae tampoco se ofrece: el
   * catálogo ya está filtrado por permiso en el servidor.
   */
  const visible = (item: NavItem): boolean => {
    if (item.permission && !may(session, item.permission)) return false;
    return item.reportKey === undefined || titles.has(item.reportKey);
  };

  return (
    <div className={plegado ? "shell plegado" : "shell"}>
      <aside className="sidebar">
        <div className="marca">
          {/* El logotipo que el administrador sube, no un isotipo escrito a
              mano. Sin uno guardado el endpoint sirve la marca de reserva, así
              que aquí no hace falta preguntar si existe. */}
          <img className="marca-iso" src="/api/brand/logo" alt="" />
          <span className="marca-nombre">
            <b>BSR</b>
            <span>Plataforma institucional</span>
          </span>
          <button
            type="button"
            className="marca-plegar"
            onClick={() => setPlegado(!plegado)}
            aria-pressed={plegado}
            aria-label={plegado ? "Desplegar el menú" : "Plegar el menú"}
            title={plegado ? "Desplegar el menú" : "Plegar el menú"}
          >
            <Icon name="plegar" />
          </button>
        </div>

        <nav className="nav" aria-label="Secciones">
          {NAV.map((group) => {
            const items = group.items.filter(visible);
            if (items.length === 0) return null;
            return (
              <div className="nav-group" key={group.title}>
                <h3>{group.title}</h3>
                {items.map((item) => (
                  <NavLink
                    className={item.admin ? "nav-link admin" : "nav-link"}
                    to={item.to}
                    key={item.to}
                    end={item.end}
                    /* Plegado, el rótulo deja de verse pero no de existir: el
                       icono nunca es el único portador del significado. */
                    title={titulo(item)}
                  >
                    <Icon name={item.icon} />
                    <span className="nav-rotulo">{titulo(item)}</span>
                  </NavLink>
                ))}
              </div>
            );
          })}
        </nav>

        <div className="sidebar-foot">
          {/* La versión del mapping, y nada más. El recuento de pendientes
              estaba también aquí, y en la franja de arriba, y en el distintivo
              de la pestaña: el mismo dato tres veces en la misma pantalla. Aquí
              se queda lo que el resto NO dice —de qué versión del mapping salen
              las cifras—, que es contexto permanente. */}
          <StatusBadge tone={config.mapping.is_draft ? "serious" : "good"}>
            mapping {config.mapping.version}
          </StatusBadge>
          <AccountMenu session={session} />
        </div>
      </aside>

      <div className="canvas">
        <Topbar config={config} miga={migaDe(pathname, titulo)} />
        <main className="main">
          <AvisoCorrida corrida={config.last_run} />
          <AvisoClasificacion resumen={config.classification} />
          <Outlet />
        </main>
      </div>
    </div>
  );
}
